// FILE: src/pages/StudentSurveyPage.jsx
// End-of-course survey (MTO 10.3, CD2.8, matrix 6.4): offered only when the
// course is finished, once per enrolment; five rated topics with optional
// comments, required improvement suggestions and free text. The server
// decides eligibility (GET /api/feedback/status).
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockClockOutlinedIcon from "@mui/icons-material/LockClockOutlined";
import { Link as RouterLink } from "react-router-dom";
import { getSurveyStatus, submitSurvey } from "../services/courseInfo";
import useAsync from "../components/help/useAsync";
import { ErrorState, LoadingState, PageHeader } from "../components/ui";
import { errorMessage } from "../i18n/errors";

export const SURVEY_TOPICS = [
  { id: "courseContent", label: "Course content", hint: "Were the lessons clear, useful and at the right level?" },
  { id: "contentSupport", label: "Course content support", hint: "Help from our instructors with questions about the lessons." },
  { id: "navigation", label: "Moving through the course", hint: "Finding your way between lessons, pages and tools." },
  { id: "technicalDifficulties", label: "Technical problems", hint: "Did the site work well on your device? (5 = no problems at all)" },
  { id: "serviceSupport", label: "Student service support", hint: "Help with your account, enrolment and records." }
];
const SCALE = [
  [1, "Very poor"],
  [2, "Poor"],
  [3, "Okay"],
  [4, "Good"],
  [5, "Excellent"]
];

function Thanks() {
  return (
    <Card sx={{ bgcolor: "zun.primaryTint", borderColor: "transparent" }} data-testid="survey-done">
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
          <CheckCircleOutlineIcon aria-hidden sx={{ color: "primary.main" }} />
          <Typography variant="h2">Thank you</Typography>
        </Stack>
        <Typography>Your answers help us improve the course for future students.</Typography>
        <Button component={RouterLink} to="/student" variant="contained" sx={{ mt: 2 }}>
          Back to my dashboard
        </Button>
      </CardContent>
    </Card>
  );
}

export default function StudentSurveyPage() {
  const status = useAsync(getSurveyStatus, []);
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [suggestions, setSuggestions] = useState("");
  const [freeText, setFreeText] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const missing = SURVEY_TOPICS.filter((t) => !ratings[t.id]).map((t) => t.id);
  const invalid = missing.length > 0 || suggestions.trim().length < 3;

  async function submit(e) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (invalid) return;
    setBusy(true);
    try {
      await submitSurvey({ ratings, comments, suggestions: suggestions.trim(), freeText: freeText.trim() });
      setDone(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const header = (
    <PageHeader
      title="Course survey"
      description="Required at the end of the course: tell us how it went. It takes about 5 minutes."
    />
  );

  if (status.loading && !status.data) return <LoadingState variant="page" label="Loading the survey…" />;
  if (status.error) {
    return (
      <>
        {header}
        <ErrorState error={status.error} onRetry={status.reload} />
      </>
    );
  }
  if (done || status.data?.submitted) {
    return (
      <>
        {header}
        <Thanks />
      </>
    );
  }
  if (!status.data?.eligible) {
    return (
      <>
        {header}
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
              <LockClockOutlinedIcon aria-hidden sx={{ color: "text.secondary" }} />
              <Typography variant="h2">Not available yet</Typography>
            </Stack>
            <Typography>The survey opens when you've finished every chapter. We'll show it on your dashboard then.</Typography>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      {header}
      <Box component="form" onSubmit={submit} noValidate>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {errorMessage(error)}
          </Alert>
        )}
        {touched && invalid && (
          <Alert severity="warning" sx={{ mb: 2 }} role="alert">
            Please rate every topic and add a suggestion before sending.
          </Alert>
        )}
        <Stack spacing={2}>
          {SURVEY_TOPICS.map((t, i) => {
            const err = touched && !ratings[t.id];
            return (
              <Card key={t.id}>
                <CardContent>
                  <FormControl component="fieldset" error={err} required sx={{ width: "100%" }}>
                    <FormLabel component="legend" sx={{ color: "text.primary", fontWeight: 650, fontSize: "1.0625rem" }}>
                      {`${i + 1}. ${t.label}`}
                    </FormLabel>
                    <Typography sx={{ color: "text.secondary", mb: 1 }}>{t.hint}</Typography>
                    <RadioGroup
                      row
                      name={t.id}
                      value={ratings[t.id] ? String(ratings[t.id]) : ""}
                      onChange={(e) => setRatings((r) => ({ ...r, [t.id]: Number(e.target.value) }))}
                      sx={{ gap: { xs: 0, sm: 1 } }}
                    >
                      {SCALE.map(([value, label]) => (
                        <FormControlLabel key={value} value={String(value)} control={<Radio />} label={`${value} · ${label}`} />
                      ))}
                    </RadioGroup>
                    {err && <FormHelperText>Choose a rating.</FormHelperText>}
                    <TextField
                      label={`Comments on ${t.label.toLowerCase()} (optional)`}
                      multiline
                      minRows={2}
                      value={comments[t.id] || ""}
                      onChange={(e) => setComments((c) => ({ ...c, [t.id]: e.target.value }))}
                      inputProps={{ maxLength: 2000 }}
                      sx={{ mt: 1.5 }}
                    />
                  </FormControl>
                </CardContent>
              </Card>
            );
          })}
          <Card>
            <CardContent>
              <Stack spacing={2.5}>
                <TextField
                  label="6. How could we improve the course?"
                  required
                  multiline
                  minRows={3}
                  value={suggestions}
                  onChange={(e) => setSuggestions(e.target.value)}
                  error={touched && suggestions.trim().length < 3}
                  helperText={touched && suggestions.trim().length < 3 ? "Add at least a few words." : " "}
                  inputProps={{ maxLength: 4000 }}
                />
                <TextField
                  label="Anything else you'd like to tell us? (optional)"
                  multiline
                  minRows={3}
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  inputProps={{ maxLength: 4000 }}
                />
              </Stack>
            </CardContent>
          </Card>
          <Box>
            <Button type="submit" variant="contained" size="large" disabled={busy}>
              {busy ? <CircularProgress size={22} color="inherit" aria-label="Sending" /> : "Send my answers"}
            </Button>
          </Box>
        </Stack>
      </Box>
    </>
  );
}
